import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Heart, DollarSign, RefreshCw, CheckCircle, Eye, Clock, User,
  Settings, Award, TrendingUp, Coins, Edit, Trash2, X, Save, 
  Printer, CreditCard, Send, Briefcase
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import api from '../../services/api';
import DOMPurify from 'dompurify';

const TipsTab = () => {
  const [tips, setTips] = useState([]);
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [view, setView] = useState('tips'); // tips, settings
  const [processing, setProcessing] = useState(false);
  const [editingSettings, setEditingSettings] = useState(false);
  const [editingTip, setEditingTip] = useState(null);
  const [editForm, setEditForm] = useState({ amount: '', note: '' });
  const [settingsForm, setSettingsForm] = useState({
    tips_enabled: true,
    min_tip_amount: 1,
    max_tip_amount: 500,
    suggested_percentages: [15, 18, 20, 25],
    show_tips_to_workers: true,
    require_approval: true
  });

  useEffect(() => {
    fetchData();
  }, [filter]);

  const fetchData = async () => {
    try {
      const params = filter ? `?status=${filter}` : '';
      const [tipsRes, statsRes, settingsRes] = await Promise.all([
        api.get(`/tips${params}`),
        api.get('/tips/stats'),
        api.get('/tips/settings')
      ]);
      setTips(tipsRes.data);
      setStats(statsRes.data);
      setSettings(settingsRes.data);
      setSettingsForm(settingsRes.data);
    } catch (error) {
      console.error('Failed to fetch tips:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveTip = async (tipId) => {
    setProcessing(true);
    try {
      await api.put(`/tips/${tipId}/approve`);
      toast.success('Tip approved and visible to worker');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve tip');
    } finally {
      setProcessing(false);
    }
  };

  const handleMarkPaid = async (tipId) => {
    setProcessing(true);
    try {
      await api.put(`/tips/${tipId}/paid`);
      toast.success('Tip marked as paid to worker');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as paid');
    } finally {
      setProcessing(false);
    }
  };

  const handleEditTip = (tip) => {
    setEditingTip(tip.id);
    setEditForm({
      amount: tip.amount.toString(),
      note: tip.note || ''
    });
  };

  const handleSaveEditTip = async () => {
    setProcessing(true);
    try {
      await api.put(`/tips/${editingTip}`, {
        amount: parseFloat(editForm.amount),
        note: editForm.note
      });
      toast.success('Tip updated successfully');
      setEditingTip(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update tip');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteTip = async (tipId) => {
    if (!window.confirm('Are you sure you want to archive this tip?')) return;
    
    setProcessing(true);
    try {
      await api.delete(`/tips/${tipId}`);
      toast.success('Tip archived successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive tip');
    } finally {
      setProcessing(false);
    }
  };

  // Add tip to worker's payroll
  const handleAddToPayroll = async (tip) => {
    setProcessing(true);
    try {
      await api.post('/payroll/add-tip', {
        worker_id: tip.worker_id,
        tip_id: tip.id,
        amount: tip.amount,
        description: `Tip from ${tip.customer_name} - ${tip.service_name}`
      });
      toast.success(`$${tip.amount.toFixed(2)} tip added to ${tip.worker_name}'s payroll`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add to payroll');
    } finally {
      setProcessing(false);
    }
  };

  // Notify worker about their tip
  const handleNotifyWorker = async (tip) => {
    setProcessing(true);
    try {
      await api.post(`/tips/${tip.id}/notify-worker`);
      toast.success(`${tip.worker_name} has been notified about their tip!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to notify worker');
    } finally {
      setProcessing(false);
    }
  };

  // Print tip receipt
  const handlePrintTip = (tip) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    
    // Sanitize user-provided content
    const safeWorkerName = DOMPurify.sanitize(tip.worker_name || '');
    const safeCustomerName = DOMPurify.sanitize(tip.customer_name || '');
    const safeServiceName = DOMPurify.sanitize(tip.service_name || '');
    const safeNote = tip.note ? DOMPurify.sanitize(tip.note) : '';
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Tip Receipt - ${safeWorkerName}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; max-width: 600px; margin: 0 auto; }
          .header { text-align: center; border-bottom: 2px solid #D4AF37; padding-bottom: 20px; margin-bottom: 20px; }
          .header h1 { color: #D4AF37; margin: 0; }
          .detail { display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #eee; }
          .amount { font-size: 32px; color: #22c55e; text-align: center; margin: 30px 0; }
          .note { background: #fdf2f8; padding: 15px; border-radius: 8px; margin: 20px 0; font-style: italic; }
          .footer { text-align: center; margin-top: 40px; color: #888; font-size: 12px; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>NC Help Hub</h1>
          <p>Tip Receipt</p>
        </div>
        <div class="amount">$${(tip.amount || 0).toFixed(2)}</div>
        <div class="detail"><span>Worker:</span><strong>${safeWorkerName}</strong></div>
        <div class="detail"><span>Customer:</span><strong>${safeCustomerName}</strong></div>
        <div class="detail"><span>Service:</span><strong>${safeServiceName}</strong></div>
        <div class="detail"><span>Date:</span><strong>${new Date(tip.created_at).toLocaleDateString()}</strong></div>
        <div class="detail"><span>Status:</span><strong>${(tip.status || '').toUpperCase()}</strong></div>
        ${safeNote ? `<div class="note">"${safeNote}"</div>` : ''}
        <div class="footer">
          <p>Thank you for your generosity!</p>
          <p>Printed: ${new Date().toLocaleString()}</p>
        </div>
      </body>
      </html>
    `;
    
    printWindow.document.open();
    printWindow.document.write(DOMPurify.sanitize(printContent, { WHOLE_DOCUMENT: true }));
    printWindow.document.close();
    printWindow.print();
  };

  const handleSaveSettings = async () => {
    setProcessing(true);
    try {
      await api.put('/tips/settings', settingsForm);
      toast.success('Tip settings updated');
      setSettings(settingsForm);
      setEditingSettings(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-[var(--warning)]/15 text-yellow-700',
      approved: 'bg-[var(--success)]/15 text-green-700',
      paid: 'bg-[#D4AF37]/10 text-[#D4AF37]'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-[var(--background-subtle)]'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="p-12 text-center" data-testid="tips-loading">
        <div className="animate-spin w-8 h-8 border-2 border-[var(--primary)] border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-[var(--text-muted)]">Loading tips...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tips-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold font-['Cormorant_Garamond'] flex items-center gap-2">
            <Heart className="w-6 h-6 text-pink-500" />
            Tips Management
          </h2>
          <p className="text-[var(--text-muted)] text-sm mt-1">Manage customer tips for workers</p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant={view === 'tips' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setView('tips')}
          >
            <Coins className="w-4 h-4 mr-1" /> Tips
          </Button>
          <Button 
            variant={view === 'settings' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setView('settings')}
          >
            <Settings className="w-4 h-4 mr-1" /> Settings
          </Button>
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      {view === 'tips' ? (
        <>
          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="card p-4 text-center border-l-4 border-pink-400">
                <p className="text-3xl font-bold text-pink-600">{stats.total_tips || 0}</p>
                <p className="text-sm text-[var(--text-muted)]">Total Tips</p>
              </div>
              <div className="card p-4 text-center border-l-4 border-green-400">
                <p className="text-3xl font-bold text-[var(--success)]">${stats.total_amount?.toFixed(2) || '0.00'}</p>
                <p className="text-sm text-[var(--text-muted)]">Total Amount</p>
              </div>
              <div className="card p-4 text-center border-l-4 border-yellow-400">
                <p className="text-3xl font-bold text-[var(--warning)]">{stats.by_status?.pending?.count || 0}</p>
                <p className="text-sm text-[var(--text-muted)]">Pending Approval</p>
              </div>
              <div className="card p-4 text-center border-l-4 border-blue-400">
                <p className="text-3xl font-bold text-[#D4AF37]">{stats.by_status?.paid?.count || 0}</p>
                <p className="text-sm text-[var(--text-muted)]">Paid Out</p>
              </div>
            </div>
          )}

          {/* Top Workers */}
          {stats?.top_workers?.length > 0 && (
            <div className="card p-4">
              <h3 className="font-bold flex items-center gap-2 mb-3">
                <Award className="w-5 h-5 text-amber-500" />
                Top Tipped Workers
              </h3>
              <div className="flex flex-wrap gap-3">
                {stats.top_workers.slice(0, 5).map((worker, idx) => (
                  <div key={worker.worker_id} className="flex items-center gap-2 bg-[var(--warning)]/10 px-3 py-2 rounded-lg">
                    <span className="text-amber-600 font-bold">#{idx + 1}</span>
                    <span className="font-medium">{worker.worker_name}</span>
                    <span className="text-[var(--success)] font-bold">${worker.total.toFixed(2)}</span>
                    <span className="text-xs text-[var(--text-muted)]">({worker.count} tips)</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="flex gap-2 flex-wrap">
            {['', 'pending', 'approved', 'paid'].map(s => (
              <Button
                key={s}
                variant={filter === s ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter(s)}
                data-testid={`filter-${s || 'all'}`}
              >
                {s === '' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
              </Button>
            ))}
          </div>

          {/* Tips List */}
          <div className="space-y-4">
            {tips.length === 0 ? (
              <div className="card p-12 text-center">
                <Heart className="w-16 h-16 text-[var(--text-muted)] mx-auto mb-4" />
                <h3 className="text-lg font-bold mb-2">No Tips Yet</h3>
                <p className="text-[var(--text-muted)]">Customer tips will appear here after services.</p>
              </div>
            ) : (
              tips.map((tip) => (
                <motion.div
                  key={tip.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`card p-5 ${tip.status === 'pending' ? 'border-2 border-yellow-300' : ''}`}
                  data-testid={`tip-${tip.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {getStatusBadge(tip.status)}
                        {tip.is_anonymous && (
                          <span className="text-xs bg-[var(--background-subtle)] text-[var(--text-secondary)] px-2 py-1 rounded-full">
                            Anonymous
                          </span>
                        )}
                        {tip.visible_to_worker && (
                          <span className="text-xs bg-[var(--success)]/15 text-[var(--success)] px-2 py-1 rounded-full flex items-center gap-1">
                            <Eye className="w-3 h-3" /> Visible to Worker
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-3xl font-bold text-[var(--success)]">${tip.amount.toFixed(2)}</div>
                        <div>
                          <p className="font-medium">{tip.service_name}</p>
                          <p className="text-sm text-[var(--text-muted)]">
                            For: <span className="font-medium">{tip.worker_name}</span>
                          </p>
                        </div>
                      </div>
                      {tip.note && (
                        <div className="mt-3 p-3 bg-pink-50 rounded-lg">
                          <p className="text-sm italic">"{tip.note}"</p>
                        </div>
                      )}
                      <div className="flex items-center gap-4 mt-3 text-xs text-[var(--text-muted)]">
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          From: {tip.customer_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(tip.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      {tip.status === 'pending' && (
                        <Button 
                          size="sm" 
                          onClick={() => handleApproveTip(tip.id)} 
                          disabled={processing}
                          className="bg-[var(--success)] hover:bg-[var(--success)]/80 text-white"
                          data-testid={`approve-tip-${tip.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" /> Approve
                        </Button>
                      )}
                      {tip.status === 'approved' && (
                        <>
                          <Button 
                            size="sm" 
                            onClick={() => handleMarkPaid(tip.id)} 
                            disabled={processing}
                            className="bg-[#D4AF37] hover:bg-[#D4AF37]/80 text-white"
                            data-testid={`pay-tip-${tip.id}`}
                          >
                            <DollarSign className="w-4 h-4 mr-1" /> Mark Paid
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleAddToPayroll(tip)} 
                            disabled={processing}
                            className="border-[var(--primary)] text-[var(--primary)]"
                            data-testid={`payroll-tip-${tip.id}`}
                          >
                            <Briefcase className="w-4 h-4 mr-1" /> Add to Payroll
                          </Button>
                        </>
                      )}
                      {/* Notify Worker */}
                      {tip.status !== 'pending' && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleNotifyWorker(tip)} 
                          disabled={processing}
                          className="text-pink-600 border-pink-300"
                          data-testid={`notify-tip-${tip.id}`}
                        >
                          <Send className="w-4 h-4 mr-1" /> Notify Worker
                        </Button>
                      )}
                      <div className="flex gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handlePrintTip(tip)}
                          className="text-[var(--text-secondary)]"
                          title="Print Receipt"
                        >
                          <Printer className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleEditTip(tip)}
                          className="text-[#D4AF37]"
                          data-testid={`edit-tip-${tip.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleDeleteTip(tip.id)}
                          className="text-[var(--error)]"
                          data-testid={`delete-tip-${tip.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </>
      ) : (
        /* Settings View */
        <div className="card p-6" data-testid="tips-settings">
          <h3 className="text-lg font-bold mb-6 flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Tip Settings
          </h3>

          <div className="space-y-6">
            {/* Enable Tips */}
            <div className="flex items-center justify-between p-4 bg-[var(--accent)] rounded-xl">
              <div>
                <p className="font-medium">Enable Tipping</p>
                <p className="text-sm text-[var(--text-muted)]">Allow customers to tip workers</p>
              </div>
              <Switch 
                checked={settingsForm.tips_enabled}
                onCheckedChange={(checked) => setSettingsForm({...settingsForm, tips_enabled: checked})}
              />
            </div>

            {/* Require Approval */}
            <div className="flex items-center justify-between p-4 bg-[var(--accent)] rounded-xl">
              <div>
                <p className="font-medium">Require Owner Approval</p>
                <p className="text-sm text-[var(--text-muted)]">Tips need your approval before workers see them</p>
              </div>
              <Switch 
                checked={settingsForm.require_approval}
                onCheckedChange={(checked) => setSettingsForm({...settingsForm, require_approval: checked})}
              />
            </div>

            {/* Show to Workers */}
            <div className="flex items-center justify-between p-4 bg-[var(--accent)] rounded-xl">
              <div>
                <p className="font-medium">Show Tips to Workers</p>
                <p className="text-sm text-[var(--text-muted)]">Workers can see their received tips</p>
              </div>
              <Switch 
                checked={settingsForm.show_tips_to_workers}
                onCheckedChange={(checked) => setSettingsForm({...settingsForm, show_tips_to_workers: checked})}
              />
            </div>

            {/* Amount Limits */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Minimum Tip Amount ($)</label>
                <Input
                  type="number"
                  value={settingsForm.min_tip_amount}
                  onChange={(e) => setSettingsForm({...settingsForm, min_tip_amount: parseFloat(e.target.value)})}
                  min="0"
                  step="0.5"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Maximum Tip Amount ($)</label>
                <Input
                  type="number"
                  value={settingsForm.max_tip_amount}
                  onChange={(e) => setSettingsForm({...settingsForm, max_tip_amount: parseFloat(e.target.value)})}
                  min="1"
                  step="10"
                />
              </div>
            </div>

            {/* Suggested Percentages */}
            <div>
              <label className="block text-sm font-medium mb-2">Suggested Tip Percentages</label>
              <div className="flex flex-wrap gap-2">
                {[10, 15, 18, 20, 25, 30].map(pct => (
                  <Button
                    key={pct}
                    variant={settingsForm.suggested_percentages?.includes(pct) ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => {
                      const current = settingsForm.suggested_percentages || [];
                      if (current.includes(pct)) {
                        setSettingsForm({...settingsForm, suggested_percentages: current.filter(p => p !== pct)});
                      } else {
                        setSettingsForm({...settingsForm, suggested_percentages: [...current, pct].sort((a,b) => a-b)});
                      }
                    }}
                  >
                    {pct}%
                  </Button>
                ))}
              </div>
              <p className="text-xs text-[var(--text-muted)] mt-1">Selected: {settingsForm.suggested_percentages?.join('%, ')}%</p>
            </div>

            {/* Save Button */}
            <div className="flex justify-end pt-4 border-t">
              <Button onClick={handleSaveSettings} disabled={processing} className="btn-primary">
                Save Settings
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Tip Modal */}
      {editingTip && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="card p-6 max-w-md w-full"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Edit className="w-5 h-5" /> Edit Tip
              </h2>
              <Button variant="ghost" size="sm" onClick={() => setEditingTip(null)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Tip Amount ($)</label>
                <Input
                  type="number"
                  value={editForm.amount}
                  onChange={(e) => setEditForm({...editForm, amount: e.target.value})}
                  step="0.01"
                  min="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Note</label>
                <textarea
                  value={editForm.note}
                  onChange={(e) => setEditForm({...editForm, note: e.target.value})}
                  className="w-full p-3 border rounded-xl min-h-[100px] resize-none"
                  placeholder="Customer's note..."
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button variant="outline" onClick={() => setEditingTip(null)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleSaveEditTip} disabled={processing} className="flex-1 btn-primary">
                <Save className="w-4 h-4 mr-2" /> Save Changes
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default TipsTab;
